import { Dialog, DialogContent } from '@/components/ui/dialog';
import { atom, useAtom, useSetAtom } from 'jotai';
import { createContext, FC, ReactElement, ReactNode, useCallback, useContext, useEffect } from 'react';

type DialogItem = {
  id: string;
  content: ReactElement;
  className?: string;
  modal?: boolean;
  dismissOnOutsideClick?: boolean;
  /** Set by the dialog itself (`useDialogSelf().setLocked`) while work must not be abandoned. */
  locked?: boolean;
};

/**
 * Per-dialog opt-ins. Every field is optional and every default reproduces
 * the behaviour this provider had before they existed, so an `openDialog`
 * call that passes nothing is byte-for-byte the dialog it always was.
 */
export type DialogOptions = {
  /** Supply a stable id instead of the generated one. */
  id?: string;
  /**
   * Radix's `modal`. Defaults to `true` — a normal blocking dialog with the
   * black scrim, a focus trap, and the rest of the page inert.
   *
   * Pass `false` ONLY for a dialog whose whole purpose is to drive something
   * behind it (the crest-line extraction dialog, whose "Draw centreline"
   * mode needs clicks to land on the 3D canvas and whose live preview is the
   * thing the user is looking at). A modal dialog makes that literally
   * impossible: Radix's own `DismissableLayer` sets
   * `document.body.style.pointerEvents = 'none'`, its `DialogOverlay` covers
   * the viewport at `z-50` with `bg-black/50`, and the first canvas click is
   * swallowed by that overlay and dismisses the dialog instead.
   *
   * `false` also implies the dialog does not dismiss on outside interaction —
   * clicking the thing it is driving must not close it. The close button
   * still works, and so does Escape — but only while focus is INSIDE the
   * dialog: the page behind is live, and an Escape meant for a canvas tool
   * (cancel a line, end an alignment walk) must not also discard the form. `modal` is fixed for a dialog's lifetime on purpose:
   * Radix swaps `DialogContentModal` for `DialogContentNonModal` when it
   * changes, which REMOUNTS the content and wipes its React state.
   */
  modal?: boolean;
  /**
   * Whether a click OUTSIDE the dialog closes it. Defaults to `true`, which is
   * Radix's behaviour and what every existing call site gets.
   *
   * Pass `false` for a dialog that holds work the user would have to redo —
   * the run-analysis dialogs (Surface Difference, Deviation, Clearance, Layer
   * Stack), which fill most of the viewport and carry a long configuration:
   * surfaces, resolution, the station corridor, crossfall bands, report
   * options. A single stray click beside one of those threw all of it away,
   * with no undo and no warning.
   *
   * This is NOT `modal: false`. The scrim and the inert page behind stay
   * exactly as they were; only the accidental dismissal goes. Escape and the
   * ✕ still close the dialog, so it never becomes a trap.
   */
  dismissOnOutsideClick?: boolean;
};

type DialogApi = {
  openDialog: (content: ReactElement, className?: string, options?: DialogOptions) => void;
  /**
   * Pops the TOP of the stack. With a non-modal dialog open, the top is not
   * necessarily the caller: the page behind stays live and can open another
   * dialog over it. A dialog that closes itself asynchronously (after a
   * mutation) should use `useDialogSelf().close` instead.
   */
  dismissActiveDialog: () => void;
  /** Closes the dialog with this id, wherever it is in the stack; no-op if gone. */
  dismissDialog: (id: string) => void;
  dismissAllDialogs: () => void;
};

const dialogStackAtom = atom<DialogItem[]>([]);

/** The id of the stack item a dialog's content is rendered in (null outside the provider). */
const DialogSelfContext = createContext<string | null>(null);

interface ProviderProps {
  children?: ReactNode;
}

const DialogProvider: FC<ProviderProps> = ({ children }) => {
  const [dialogs, setDialogs] = useAtom(dialogStackAtom);

  const _handleCloseById = (id: string) => setDialogs((prev) => prev.filter((d) => d.id !== id));

  // Freeze the page behind the stack — but only while something in it is
  // actually modal. With no dialogs, or with only non-modal ones, the body
  // stays interactive. `modal !== false` (not `modal === true`) keeps the
  // default path identical: every existing caller leaves `modal` undefined.
  useEffect(() => {
    const anyModal = dialogs.some((d) => d.modal !== false);
    document.body.style.setProperty('pointer-events', anyModal ? 'none' : 'auto');
  }, [dialogs]);

  return (
    <>
      {children}
      {dialogs.map((item) => (
        <Dialog
          data-id={item.id}
          key={item.id}
          open
          // Undefined → true, which is Radix's own default: unchanged for
          // every dialog that does not opt out.
          modal={item.modal ?? true}
          onOpenChange={(open) => {
            if (!open && !item.locked) _handleCloseById(item.id);
          }}>
          <DialogContent
            className={item.className}
            // A locked dialog (an import running) has no ✕; Escape and outside clicks do nothing.
            showCloseButton={!item.locked}
            // Two independent reasons an outside pointer-down is not a
            // dismissal, handled by the same guard:
            //   - a NON-MODAL dialog exists to drive what is behind it, so
            //     the click is a legitimate interaction;
            //   - a dialog that opted out of `dismissOnOutsideClick` holds
            //     work a misclick must not discard.
            // Radix's `DismissableLayer` calls `onDismiss()` unless
            // `onInteractOutside` is default-prevented — and it does so in
            // non-modal mode too. Escape and the close button remain in both
            // cases. Everything else gets no handler at all, so the default
            // dismissal is untouched.
            {...(item.modal === false || item.dismissOnOutsideClick === false
              ? { onInteractOutside: (event: Event) => event.preventDefault() }
              : {})}
            // A non-modal dialog shares the keyboard with the live page
            // behind it: Escape closes it only while focus is inside it
            // (UX review L-1). Radix listens on the document, so without this
            // an Escape that cancels a canvas tool also wipes the form.
            {...(item.modal === false
              ? {
                  'data-dialog-id': item.id,
                  onEscapeKeyDown: (event: KeyboardEvent) => {
                    const target = event.target instanceof Element ? event.target : null;
                    if (target?.closest('[data-dialog-id]')?.getAttribute('data-dialog-id') !== item.id)
                      event.preventDefault();
                  },
                }
              : {})}
            {...(item.locked
              ? {
                  onEscapeKeyDown: (event: KeyboardEvent) => event.preventDefault(),
                  onInteractOutside: (event: Event) => event.preventDefault(),
                }
              : {})}>
            <DialogSelfContext.Provider value={item.id}>{item.content}</DialogSelfContext.Provider>
          </DialogContent>
        </Dialog>
      ))}
    </>
  );
};

export const useDialog = (): DialogApi => {
  const [, setDialogs] = useAtom(dialogStackAtom);

  const openDialog = useCallback(
    (content: ReactElement, className?: string, options?: DialogOptions) => {
      const id =
        options?.id ||
        (typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`);
      setDialogs((prev) => [
        ...prev,
        { id, content, className, modal: options?.modal, dismissOnOutsideClick: options?.dismissOnOutsideClick },
      ]);
    },
    [setDialogs],
  );

  const dismissActiveDialog = useCallback(
    () => setDialogs((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev)),
    [setDialogs],
  );

  const dismissDialog = useCallback(
    (id: string) => setDialogs((prev) => (prev.some((d) => d.id === id) ? prev.filter((d) => d.id !== id) : prev)),
    [setDialogs],
  );

  const dismissAllDialogs = useCallback(() => setDialogs([]), [setDialogs]);

  return { openDialog, dismissActiveDialog, dismissDialog, dismissAllDialogs };
};

/**
 * For a dialog's own content: its stack id and a `close` that removes THAT
 * dialog, not whichever is on top (UX review M-2: a non-modal dialog whose
 * run resolved after another dialog opened over it used to pop the other
 * one and stay open, Run armed, for a duplicate paid run). Safe to call
 * after the dialog is already gone. Outside the provider `close` does nothing.
 */
export const useDialogSelf = (): { id: string | null; close: () => void; setLocked: (locked: boolean) => void } => {
  const id = useContext(DialogSelfContext);
  const { dismissDialog } = useDialog();
  const setDialogs = useSetAtom(dialogStackAtom);
  const close = useCallback(() => {
    if (id != null) dismissDialog(id);
  }, [id, dismissDialog]);
  /** While locked, the dialog's ✕, Escape and outside clicks do not close it; `close` still does. */
  const setLocked = useCallback(
    (locked: boolean) => setDialogs((prev) => prev.map((d) => (d.id === id ? { ...d, locked } : d))),
    [id, setDialogs],
  );
  return { id, close, setLocked };
};

export default DialogProvider;
